import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Info, Loader2, ScrollText, Search } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { PagoRegistrado } from '@/shared/api/tipos';
import { Modal } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { PanelDeCobro } from '@/modules/recaudacion/ModalDeCobro';
import { Campo, ENTRADA, ListaDeTitulos, SeleccionDePersona, type Deudor } from './piezas';

interface ProximoComprobante {
  numero: string;
  caja: string | null;
  nombreDeLaCaja: string | null;
}

interface Identidad {
  nombre: string;
  direccion?: string | null;
  telefono?: string | null;
  correo?: string | null;
  fallecido: boolean;
}

export function ModalDeCobroDeTitulos({
  textoInicial = '',
  alCerrar,
  alCobrado,
}: {
  textoInicial?: string;
  alCerrar: () => void;
  alCobrado: (pago: PagoRegistrado) => void;
}) {
  const { perfil, puede } = useSesion();

  const [busqueda, setBusqueda] = useState(textoInicial);
  const [deudor, setDeudor] = useState<Deudor | null>(null);
  const [desmarcados, setDesmarcados] = useState<Set<number>>(new Set());

  const [pagaOtro, setPagaOtro] = useState(false);
  const [identificacion, setIdentificacion] = useState('');
  const [nombre, setNombre] = useState('');
  const [direccion, setDireccion] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [fallecido, setFallecido] = useState(false);

  const [metodo, setMetodo] = useState('efectivo');
  const [numeroComprobante, setNumeroComprobante] = useState('');
  const [comprobanteTocado, setComprobanteTocado] = useState(false);
  const [referenciaBancaria, setReferenciaBancaria] = useState('');
  const [banco, setBanco] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<string | null>(null);

  const proximo = useQuery({
    queryKey: ['proximo-comprobante'],
    queryFn: () => api.get<ProximoComprobante>('/cobros/proximo-comprobante'),
    enabled: deudor !== null,
    staleTime: 0,
    gcTime: 0,
  });

  const debeIdentificarse = deudor !== null && (pagaOtro || !deudor.identificacion);
  const pagador = debeIdentificarse
    ? { identificacion: identificacion.trim(), nombre: nombre.trim() }
    : { identificacion: deudor?.identificacion ?? '', nombre: deudor?.nombre ?? '' };
  const pagadorValido = /^(\d{10}|\d{13})$/.test(pagador.identificacion) && pagador.nombre !== '';

  const seleccion = deudor?.titulos.filter((t) => !desmarcados.has(t.id)) ?? [];
  const total = seleccion.reduce((suma, t) => suma + t.capital, 0);

  const registroCivil = useMutation({
    mutationFn: () => api.get<Identidad>(`/identidades/${identificacion.trim()}`),
    onSuccess: (i) => {
      setNombre(i.nombre);
      if (i.direccion) setDireccion(i.direccion);
      if (i.telefono) setTelefono(i.telefono);
      if (i.correo) setEmail(i.correo);
      setFallecido(i.fallecido);
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO CONSULTAR'),
  });

  const cobrar = useMutation({
    mutationFn: () =>
      api.post<PagoRegistrado>('/cobros/titulos', {
        titulos: seleccion.map((t) => t.id),
        pagador: {
          ...pagador,
          direccion: debeIdentificarse ? direccion.trim() || undefined : undefined,
          telefono: debeIdentificarse ? telefono.trim() || undefined : undefined,
          email: debeIdentificarse ? email.trim() || undefined : undefined,
        },
        metodo,
        numeroComprobante: comprobanteTocado ? numeroComprobante.trim() || undefined : undefined,
        referenciaBancaria: referenciaBancaria.trim() || undefined,
        banco: metodo === 'cheque' ? banco.trim() || undefined : undefined,
        observaciones: observaciones.trim() || undefined,
      }),
    onSuccess: (pago) => {
      notificar.exito(
        `Cobrado $${pago.total.toFixed(2)}${pago.numeroComprobante ? ` · ${pago.numeroComprobante}` : ''}`,
      );
      alCobrado(pago);
    },
    onError: (causa) => {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo registrar el cobro';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  function elegir(d: Deudor) {
    setDeudor(d);
    setDesmarcados(new Set());
    setPagaOtro(false);
    setIdentificacion('');
    setNombre(d.identificacion ? '' : (d.nombre ?? ''));
    setDireccion('');
    setTelefono('');
    setEmail('');
    setFallecido(false);

    setNumeroComprobante('');
    setComprobanteTocado(false);
    setReferenciaBancaria('');
    setObservaciones('');
    setError(null);
    cobrar.reset();
  }

  const listo = seleccion.length > 0 && pagadorValido && !cobrar.isPending;
  const caja = proximo.data?.nombreDeLaCaja ?? null;

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Registrar cobro · Art. 8"
      subtitulo={
        perfil?.nombre ? `Cajero: ${perfil.nombre}${caja ? ` · ${caja}` : ''}` : (caja ?? '')
      }
      icono={ScrollText}
      ancho="ventanilla"
      cerrarAlPulsarFuera={false}
    >
      <SeleccionDePersona
        pregunta="Quién viene a pagar"
        busqueda={busqueda}
        alBuscar={setBusqueda}
        deudor={deudor}
        alElegir={elegir}
        alLimpiar={() => {
          setDeudor(null);
          setBusqueda('');
        }}
        textoInicial={textoInicial}
      />

      {deudor === null ? (
        <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
          Busque a la persona que viene a pagar
        </p>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[1fr_20rem]">
          <div className="space-y-4">
            <p className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-xs uppercase leading-relaxed text-blue-800">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>
                <span className="font-semibold">Títulos heredados del SIRE, sin interés.</span> Por
                resolución de EPAGAL se cobra solo el capital.
              </span>
            </p>

            <ListaDeTitulos
              titulos={deudor.titulos}
              desmarcados={desmarcados}
              alCambiar={setDesmarcados}
            />

            <div className="rounded-2xl border border-armazon-200 bg-white p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] font-bold uppercase tracking-wide text-armazon-600">
                  Quién paga · la factura sale a su nombre
                </p>
                {deudor.identificacion && (
                  <button
                    type="button"
                    onClick={() => setPagaOtro(!pagaOtro)}
                    className="text-[11px] font-semibold uppercase text-marca-700 hover:underline"
                  >
                    {pagaOtro ? 'Paga el titular' : 'Paga otra persona'}
                  </button>
                )}
              </div>

              {!debeIdentificarse ? (
                <p className="mt-2 text-xs uppercase text-armazon-700">
                  El titular · <span className="importe">{deudor.identificacion}</span>
                </p>
              ) : (
                <>
                  {!deudor.identificacion && (
                    <p className="mt-2 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] uppercase text-amber-800">
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                      El SIRE guardó este título sin cédula: identifique a quien paga.
                    </p>
                  )}
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <Campo etiqueta="Cédula o RUC">
                      <span className="flex gap-2">
                        <input
                          value={identificacion}
                          onChange={(e) => {
                            setIdentificacion(e.target.value.replace(/\D/g, ''));
                            setFallecido(false);
                          }}
                          maxLength={13}
                          inputMode="numeric"
                          className={`${ENTRADA} importe`}
                        />
                        {puede('identidades.consultar') && (
                          <button
                            type="button"
                            onClick={() => registroCivil.mutate()}
                            disabled={
                              !/^(\d{10}|\d{13})$/.test(identificacion.trim()) ||
                              registroCivil.isPending
                            }
                            title="Traer el nombre del Registro Civil"
                            className="inline-flex h-10 shrink-0 items-center gap-1 rounded-lg border border-armazon-200 px-3 text-[11px] font-semibold uppercase text-armazon-600 transition-colors hover:bg-armazon-50 disabled:opacity-40"
                          >
                            {registroCivil.isPending ? (
                              <Loader2 className="size-3.5 animate-spin" aria-hidden />
                            ) : (
                              <Search className="size-3.5" aria-hidden />
                            )}
                            Registro Civil
                          </button>
                        )}
                      </span>
                    </Campo>
                    <Campo etiqueta="Nombre">
                      <input
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        maxLength={255}
                        className={`${ENTRADA} uppercase`}
                      />
                    </Campo>
                    <Campo etiqueta="Dirección (opcional)">
                      <input
                        value={direccion}
                        onChange={(e) => setDireccion(e.target.value)}
                        maxLength={500}
                        className={ENTRADA}
                      />
                    </Campo>
                    <span className="grid grid-cols-2 gap-3">
                      <Campo etiqueta="Teléfono">
                        <input
                          value={telefono}
                          onChange={(e) => setTelefono(e.target.value)}
                          maxLength={20}
                          className={ENTRADA}
                        />
                      </Campo>
                      <Campo etiqueta="Correo">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          maxLength={255}
                          className={ENTRADA}
                        />
                      </Campo>
                    </span>
                  </div>
                  {fallecido && (
                    <p className="mt-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] uppercase text-amber-800">
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                      El Registro Civil registra la defunción de esta persona. Confirme quién paga.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <PanelDeCobro
            unidad={['título', 'títulos']}
            vacio="Ningún título marcado"
            seleccionadas={seleccion.length}
            capital={total}
            interes={0}
            metodo={metodo}
            alCambiarMetodo={setMetodo}
            numeroComprobante={comprobanteTocado ? numeroComprobante : (proximo.data?.numero ?? '')}
            alCambiarComprobante={(v) => {
              setComprobanteTocado(true);
              setNumeroComprobante(v);
            }}
            caja={caja}
            sinCaja={proximo.isSuccess && !proximo.data.caja}
            referenciaBancaria={referenciaBancaria}
            banco={banco}
            alCambiarBanco={setBanco}
            alCambiarReferencia={setReferenciaBancaria}
            observaciones={observaciones}
            alCambiarObservaciones={setObservaciones}
            error={error ?? (seleccion.length > 0 && !pagadorValido ? FALTA_PAGADOR : null)}
            cobrando={cobrar.isPending}
            listo={listo}
            alCobrar={() => {
              setError(null);
              cobrar.mutate();
            }}
          />
        </div>
      )}
    </Modal>
  );
}

const FALTA_PAGADOR = 'FALTA LA CÉDULA O EL RUC Y EL NOMBRE DE QUIEN PAGA.';
